/**
 * Project availability window and derived lifecycle status.
 *
 * A project's operational availability is governed by its mandatory
 * availability window: `projectStartDate` (first fully available day) and
 * `projectCloseDate` (last fully available day). The window is compared
 * against the business date in `Asia/Kolkata` — the single time zone used
 * for every project-status calculation in LabourFlow — and never against a
 * raw server clock.
 *
 * Legacy projects created before availability dates existed have NULL start /
 * close dates. They are treated as active/compatible until an Organization
 * Admin supplies dates, so historical workflows and dropdowns keep working.
 */
export const PROJECT_AVAILABILITY = {
  ACTIVE: "active",
  UPCOMING: "upcoming",
  CLOSED: "closed",
} as const;

export type ProjectAvailability = (typeof PROJECT_AVAILABILITY)[keyof typeof PROJECT_AVAILABILITY];

/** IANA time zone that defines the LabourFlow business day. */
export const BUSINESS_TIME_ZONE = "Asia/Kolkata";

/** Date part of `now` in the given IANA time zone, formatted YYYY-MM-DD. */
export function businessDateIn(timeZone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** Current business date (YYYY-MM-DD) in Asia/Kolkata. */
export function getBusinessDate(now: Date = new Date()): string {
  return businessDateIn(BUSINESS_TIME_ZONE, now);
}

/** Date-only compare of two YYYY-MM-DD strings: -1, 0 or 1. */
export function compareDateOnly(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  if (ay !== by) return ay < by ? -1 : 1;
  if (am !== bm) return am < bm ? -1 : 1;
  if (ad !== bd) return ad < bd ? -1 : 1;
  return 0;
}

/**
 * Derives the lifecycle status of a project from its availability window and
 * the business date:
 *  - upcoming: business date is before projectStartDate
 *  - closed:   business date is after projectCloseDate (the close date is the
 *              last fully available day, so close === business date is active)
 *  - active:   everything in between (including start === close === today)
 * Legacy projects missing either date are treated as active/compatible.
 */
export function deriveProjectAvailability(
  projectStartDate: string | null | undefined,
  projectCloseDate: string | null | undefined,
  businessDate: string = getBusinessDate()
): ProjectAvailability {
  if (!projectStartDate || !projectCloseDate) return PROJECT_AVAILABILITY.ACTIVE;
  if (compareDateOnly(businessDate, projectStartDate) < 0) return PROJECT_AVAILABILITY.UPCOMING;
  if (compareDateOnly(businessDate, projectCloseDate) > 0) return PROJECT_AVAILABILITY.CLOSED;
  return PROJECT_AVAILABILITY.ACTIVE;
}

/**
 * SQL fragment (with bound params) restricting a projects query to currently
 * available projects: inside the date window OR legacy null-date
 * compatibility. Columns are YYYY-MM-DD TEXT, so the lexicographic `<=` / `>=`
 * comparisons match date ordering. The caller adds its own organization and
 * archived-status predicates.
 */
export function availabilityActiveSql(
  alias: string,
  businessDate: string = getBusinessDate()
): { sql: string; params: string[] } {
  return {
    sql: `(${alias}.project_start_date IS NULL OR ${alias}.project_close_date IS NULL OR (${alias}.project_start_date <= ? AND ${alias}.project_close_date >= ?))`,
    params: [businessDate, businessDate],
  };
}
